import {
  LinkField,
  Text,
  Field,
  useComponentProps,
  GetServerSideComponentProps,
  withDatasourceCheck,
  ComponentRendering,
} from "@sitecore-jss/sitecore-jss-nextjs";
import { Product } from "@repo/apis/browse-search-v2";
import { getRequestContext } from "@repo/sitecore-lib/store-presentation";
import * as Container from "@repo/ui/container";
import * as Grid from "@repo/ui/grid";
import * as Viewport from "@/components/viewport";
import { configurePageRouterServerRequest } from "@/utils/request-adapters/page-router-server-request";
import { ComponentProps } from "../../../types/component-props";
import AnimatedCtaLink from "../../animated-cta-link/animated-cta-link";
import { appendRef } from "../../utils";
import { fetchProductsData } from "./fetch-bulk-products";
import { fetchCategoryProducts } from "./fetch-category-products";
import ProductView from "./product-view";
import { EditorialProduct } from "./product-view";
import styles from "./product-grid.module.css";

type SitecoreProduct = {
  id: string;
  name: string;
  path?: string;
  fields: [
    { name: "productTitle"; jsonValue: Field<string> },
    { name: "productIdentifier"; jsonValue: Field<string> },
    { name: "colour"; jsonValue: Field<string> },
  ];
};

type ProductGridDatasource = {
  ctaLink: { jsonValue: LinkField };
  ctaText: Field<string>;
  ctaRef: Field<string>;
  header: Field<string>;
  categoryId: Field<string>;
  limit: Field<string>;
  products: { results: Array<SitecoreProduct> };
};

type ProductGridFields = {
  fields: {
    data: {
      datasource: ProductGridDatasource;
    };
  };
};

export type ProductGridProps = ComponentProps & ProductGridFields;

type ServerData = {
  dataProducts: EditorialProduct[];
};

const ProductGrid = (props: ProductGridProps) => {
  const {
    fields: {
      data: {
        datasource: {
          ctaLink: { jsonValue: headerLink },
          ctaText: headerLinkText,
          ctaRef: headerLinkRef,
          header: headerTitle,
        },
      },
    },
  } = props;

  const ctaLinkWithRef = appendRef(headerLink.value.href!, headerLinkRef.value);

  const data = useComponentProps<ServerData>(props.rendering.uid);
  const dataProducts = data?.dataProducts as EditorialProduct[] | undefined;
  if (!dataProducts?.length) return <></>;

  return (
    <Container.Root size="medium">
      <Grid.Container columns={2} className={styles.header}>
        <Grid.Item>
          <Text field={headerTitle} className="typographyHeading1" tag="p" />
        </Grid.Item>
        <Grid.Item className={styles.gridCta}>
          <AnimatedCtaLink
            className="typographyLink2Caps"
            link={{
              ...headerLink,
              value: {
                ...headerLink.value,
                href: ctaLinkWithRef,
                text: headerLinkText?.value,
              },
            }}
          />
        </Grid.Item>
      </Grid.Container>
      <Viewport.Small>
        <Grid.Container data-testid="mobile-grid" columns={2} rowGap={"md"}>
          {dataProducts.map((p, index) => {
            return <ProductView key={`${index}-${p.id}`} product={p} />;
          })}
        </Grid.Container>
      </Viewport.Small>
      <Viewport.Large data-testid="desktop-grid">
        <Grid.Container
          columns={dataProducts.length <= 4 ? dataProducts.length : 4}
        >
          {dataProducts.map((p, index) => {
            return <ProductView key={`${index}-${p.id}`} product={p} />;
          })}
        </Grid.Container>
      </Viewport.Large>
    </Container.Root>
  );
};

export const getServerSideProps: GetServerSideComponentProps = async (
  rendering,
  _layoutData,
  context,
) => {
  const req = context?.req;
  if (!req) return { dataProducts: [] as Product[] };

  const serverRequest = configurePageRouterServerRequest({ req });

  const componentName = (rendering as ComponentRendering).componentName;
  const ds: ProductGridDatasource = (rendering as unknown as ProductGridFields)
    .fields?.data?.datasource;

  let idsToFetch: number[];
  let productData: Product[];

  try {
    const requestContext = await getRequestContext(req);

    const {
      countryData: {
        storeCode: store,
        defaultCurrency: currency,
        countryCode: country,
        defaultLanguage: lang,
        defaultSizeSchema: sizeSchema,
      },
      keyStoreDataversion,
      shop,
    } = requestContext;

    if (componentName === "Category Product Grid") {
      idsToFetch = [Number(ds.categoryId.value)];

      const hasCategoryIds = idsToFetch.length > 0;

      if (!hasCategoryIds) {
        return { dataProducts: [] as Product[] };
      }

      const limits = [Number(ds.limit.value)];

      productData = await fetchCategoryProducts({
        categoryIds: idsToFetch,
        limits,
        requestInstance: serverRequest,
        store,
        currency,
        keyStoreDataversion,
        country,
        lang,
        shop,
      });
      return { dataProducts: productData };
    }

    if (componentName === "Product Grid") {
      const sitecoreProducts: SitecoreProduct[] = ds?.products?.results ?? [];

      idsToFetch = sitecoreProducts.length
        ? sitecoreProducts
            .map((product) => {
              const idField = product.fields.find(
                (f) => f.name === "productIdentifier",
              );
              return +idField!.jsonValue.value;
            })
            .filter((n) => Number.isFinite(n))
        : [];

      const hasProductIds = idsToFetch.length > 0;

      if (!hasProductIds) {
        return { dataProducts: [] as Product[] };
      }

      productData = await fetchProductsData({
        productsIdentifiers: idsToFetch,
        store,
        keyStoreDataversion,
        country,
        lang,
        shop,
        currency,
        sizeSchema,
        requestInstance: serverRequest,
      });

      return { dataProducts: productData };
    }
  } catch (err) {
    console.log("Editorial Product Grid Error:", { err });
    return { dataProducts: [] as Product[] };
  }
};

export default withDatasourceCheck()<ProductGridProps>(ProductGrid);
